import React, { useEffect } from 'react'
import { Lock, LogIn, UserX, ArrowRight, AlertCircle } from 'lucide-react'

export interface AccessCheckResponse {
  hasAccess: boolean
  requiresLogin: boolean
  message?: string
  redirectUrl?: string
  requiredAccountTypes?: string[]
  requiredDesignations?: string[]
  showCustomMessage?: boolean
}

interface RestrictedEventAccessProps {
  eventSlug: string
  children: React.ReactNode
  accessCheck?: AccessCheckResponse
  isLoading?: boolean
  // Optional style overrides for when EventContext isn't available (e.g., registration app)
  primaryColor?: string
  buttonBgColor?: string
  buttonTextColor?: string
  eventName?: string
}

export const RestrictedEventAccess: React.FC<RestrictedEventAccessProps> = ({
  eventSlug,
  children,
  accessCheck,
  isLoading = false,
  primaryColor = '#0066CC',
  buttonBgColor = '#ffffff',
  buttonTextColor,
  eventName = 'event'
}) => {
  // Use computed button text color if not provided
  const computedButtonTextColor = buttonTextColor || primaryColor

  // Handle redirect if configured
  useEffect(() => {
    // If access is denied and we have a redirect URL and custom message is disabled,
    // redirect immediately without showing the modal
    if (!accessCheck?.hasAccess && accessCheck?.redirectUrl && !accessCheck?.showCustomMessage) {
      window.location.href = accessCheck.redirectUrl
    }
  }, [accessCheck])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading event...</p>
        </div>
      </div>
    )
  }

  // If access is granted or no access check provided, render content normally
  if (accessCheck?.hasAccess !== false) {
    return <>{children}</>
  }

  // If we're about to redirect (no custom message and have redirect URL), show loading
  if (!accessCheck?.showCustomMessage && accessCheck?.redirectUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting...</p>
        </div>
      </div>
    )
  }

  // Render restriction overlay with optional custom message
  return (
    <>
      {/* Custom message if enabled */}
      {accessCheck.showCustomMessage && (
        <div className="fixed inset-0 overflow-hidden">
          <div style={{ filter: 'blur(8px)', opacity: 0.6, transform: 'scale(1.1)' }}>

          </div>
        </div>
      )}

      {/* Restriction message overlay */}
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/40 p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl">
          <div className="text-center">
            {/* Icon with theme color */}
            <div
              className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              {accessCheck.requiresLogin ? (
                <LogIn className="w-8 h-8" style={{ color: primaryColor }} />
              ) : (
                <Lock className="w-8 h-8" style={{ color: primaryColor }} />
              )}
            </div>

            <h2 className="text-2xl font-bold mb-3 text-gray-900">
              {accessCheck.requiresLogin ? 'Sign In Required' : 'Access Restricted'}
            </h2>

            {/* Custom message with HTML support */}
            {accessCheck.message ? (
              <div
                className="text-gray-600 mb-6 prose prose-sm max-w-none text-center"
                dangerouslySetInnerHTML={{ __html: accessCheck.message }}
              />
            ) : (
              <p className="text-gray-600 mb-6">
                {accessCheck.requiresLogin
                  ? `You must be signed in to access this ${eventName}.`
                  : `This ${eventName} requires special access permissions.`
                }
              </p>
            )}

            {/* Required account types badges */}
            {accessCheck.requiredAccountTypes?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Required Account Types:</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {accessCheck.requiredAccountTypes.map((type, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 rounded-full text-sm font-medium"
                      style={{
                        backgroundColor: `${primaryColor}15`,
                        color: primaryColor,
                        border: `1px solid ${primaryColor}30`
                      }}
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Required designations badges */}
            {accessCheck.requiredDesignations?.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 mb-2">Required Designations:</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {accessCheck.requiredDesignations.map((designation, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 rounded-full text-sm font-medium"
                      style={{
                        backgroundColor: `${primaryColor}15`,
                        color: primaryColor,
                        border: `1px solid ${primaryColor}30`
                      }}
                    >
                      {designation}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Action buttons with theme colors */}
            <div className="flex flex-col gap-3">
              {accessCheck.requiresLogin ? (
                <>
                  <a
                    href="/accounts/sign_in"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105 shadow-lg"
                    style={{
                      backgroundColor: buttonBgColor,
                      color: computedButtonTextColor,
                      border: buttonBgColor === '#ffffff' ? `1px solid ${primaryColor}30` : 'none'
                    }}
                  >
                    <LogIn className="w-5 h-5" />
                    Sign In to Continue
                  </a>
                  <a
                    href="/accounts/sign_up"
                    className="px-6 py-3 rounded-lg font-medium transition-colors border-2"
                    style={{
                      borderColor: primaryColor,
                      color: primaryColor
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${primaryColor}10`
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    Create Account
                  </a>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      // Always navigate to the root of the current subdomain
                      window.location.href = '/'
                    }}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105 shadow-lg"
                    style={{
                      backgroundColor: buttonBgColor,
                      color: computedButtonTextColor,
                      border: buttonBgColor === '#ffffff' ? `1px solid ${primaryColor}30` : 'none'
                    }}
                  >
                    <ArrowRight className="w-5 h-5 rotate-180" />
                    Return to Home
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}